import { NgClass, NgIf } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';

export type ConfirmDialogVariant = 'info' | 'warning' | 'danger' | 'success';

export interface ConfirmDialogData {
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  showCancel?: boolean;
  variant?: ConfirmDialogVariant;
}

@Component({
  selector: 'app-confirm-dialog',
  standalone: true,
  imports: [MatDialogModule, MatButtonModule, MatIconModule, NgIf, NgClass],
  templateUrl: './confirm-dialog.html',
  styleUrl: './confirm-dialog.scss',
})
export class ConfirmDialog {
  constructor(
    public dialogRef: MatDialogRef<ConfirmDialog>,
    @Inject(MAT_DIALOG_DATA) public data: ConfirmDialogData
  ) {}

  get showCancel(): boolean {
    return this.data.showCancel !== false;
  }

  get confirmText(): string {
    return (this.data.confirmText ?? 'OK').trim() || 'OK';
  }

  get cancelText(): string {
    return (this.data.cancelText ?? 'Cancel').trim() || 'Cancel';
  }

  get icon(): string | null {
    const v = this.data.variant ?? 'info';
    if (v === 'success') return 'check_circle';
    if (v === 'danger') return 'error';
    if (v === 'warning') return 'warning';
    return 'info';
  }

  get iconClass(): string {
    const v = this.data.variant ?? 'info';
    if (v === 'success') return 'text-green-600';
    if (v === 'danger') return 'text-red-600';
    if (v === 'warning') return 'text-amber-600';
    return 'text-blue-600';
  }

  get iconBgClass(): string {
    const v = this.data.variant ?? 'info';
    if (v === 'success') return 'bg-green-50';
    if (v === 'danger') return 'bg-red-50';
    if (v === 'warning') return 'bg-amber-50';
    return 'bg-blue-50';
  }

  get confirmButtonClass(): string {
    const v = this.data.variant ?? 'info';
    if (v === 'success') return 'border-green-500 text-green-700 hover:bg-green-50';
    if (v === 'danger') return 'border-red-500 text-red-600 hover:bg-red-50';
    if (v === 'warning') return 'border-amber-500 text-amber-700 hover:bg-amber-50';
    return 'border-blue-500 text-blue-700 hover:bg-blue-50';
  }

  confirm(): void {
    this.dialogRef.close(true);
  }

  cancel(): void {
    this.dialogRef.close(false);
  }
}
